import { Component, ChangeDetectionStrategy, OnChanges, input, output } from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import type { ProductSKU } from '@fitness-force/models';

export interface ProductFulfillmentItem {
  sku_id: number;
  product_sku?: ProductSKU;
  quantity: number;
  is_plan_sku: boolean;
  is_fulfilled: boolean;
}

@Component({
  selector: 'product-fulfillment-popup',
  templateUrl: './product-fulfillment-popup.component.html',
  styleUrls: ['./product-fulfillment-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    ToggleButtonModule,
    FormsModule,
    TranslateModule,
    CurrencyformatPipe
]
})
export class ProductFulfillmentPopupComponent implements OnChanges {
  readonly visible = input(false);
  readonly products = input<ProductFulfillmentItem[]>([]);
  
  readonly visibleChange = output<boolean>();
  readonly confirmFulfillment = output<ProductFulfillmentItem[]>();
  readonly cancelFulfillment = output<void>();

  localProducts: ProductFulfillmentItem[] = [];
  imageErrors = new Set<number>();

  ngOnChanges() {
    // Create a deep copy of products to avoid mutating the original array
    this.localProducts = this.products().map(product => ({ ...product }));
    // Reset image errors when products change
    this.imageErrors.clear();
  }

  onToggleFulfillment(product: ProductFulfillmentItem, fulfilled: boolean) {
    product.is_fulfilled = fulfilled;
  }

  onConfirm() {
    this.confirmFulfillment.emit(this.localProducts);
    this.hideDialog();
  }

  onCancel() {
    this.cancelFulfillment.emit();
    this.hideDialog();
  }

  hideDialog() {
    this.visibleChange.emit(false);
  }

  getProductImage(product: ProductFulfillmentItem): string {
    return product.product_sku?.image_url || '';
  }

  getProductName(product: ProductFulfillmentItem): string {
    return product.product_sku?.sku_name_primary_language || 
           product.product_sku?.product_name_primary || 
           `SKU ${product.sku_id}`;
  }

  getProductPrice(product: ProductFulfillmentItem): number {
    return (product.product_sku?.unit_price_minor || 0) * product.quantity;
  }

  getUnitPrice(product: ProductFulfillmentItem): number {
    return product.product_sku?.unit_price_minor || 0;
  }

  getFulfilledCount(): number {
    return this.localProducts.filter(p => p.is_fulfilled).length;
  }

  getPendingCount(): number {
    return this.localProducts.filter(p => !p.is_fulfilled).length;
  }

  trackBySkuId(index: number, product: ProductFulfillmentItem): number {
    return product.sku_id;
  }

  onImageError(product: ProductFulfillmentItem): void {
    // Mark this product's image as failed to load
    this.imageErrors.add(product.sku_id);
  }

  hasImageError(product: ProductFulfillmentItem): boolean {
    return this.imageErrors.has(product.sku_id);
  }

  shouldShowImage(product: ProductFulfillmentItem): boolean {
    return !!(this.getProductImage(product) && !this.hasImageError(product));
  }
}
